/*eslint new-cap:0 */
import ko from 'knockout';
import _ from 'underscore';
import { ExpandedTextAd, DisclaimerAdExtension } from '@bingads-webui-campaign/campaign-ui-schemas';
import dynamicTextExtension from '@bingads-webui-campaign-legacy/crud-ad/viewmodel/adsEditor/dynamicTextExtension';
import KoSchema from '@bingads-webui-component-legacy/ko-schema/index';
import config from '@bingads-webui-component-legacy/config/index';
import parsedTextMaxLengthExtension from '@bingads-webui-campaign-legacy/crud-ad/viewmodel/adsEditor/parsedTextMaxLengthExtension';
import { getFinalUrlDomain } from '@bingads-webui-campaign-legacy/crud-ad/service/displayUrl';
import edmCampaign from '@bingads-webui-component-legacy/edm-campaign/index';
import jsDataCampaign from '@bingads-webui-component-legacy/js-data-campaign/index';
import SharedRepository from '@bingads-webui-campaign-legacy/adextension-shared/service/repository';
import pageContext from '@bingads-webui-static-legacy/page-context';

const i18n = window.globalLegacyI18n.get('component/i18n/loader!campaignui/campaign-creation-advanced');

const permissions = config.get('Permissions') || {};
const {
  IsDisclaimerAdsEnabled,
} = permissions;

export default class ViewModel {
  constructor({
    model = null,
    isNativeAdsEnabled = false,
    isVanityUrlEnabled = false,
    IsExpandedTextAdV2Enabled = false,
    placeholders = {},
    scope = null,
    activityLogger,
    perfMarker,
  } = {}) {
    // Babel would understand the intent and do this behind the curtain but it is better not to rely on it.
    const self = this;

    this.model = model;
    this.placeholders = placeholders; // preview uses this
    this.IsExpandedTextAdV2Enabled = IsExpandedTextAdV2Enabled;
    this.scope = scope;
    this.activityLogger = activityLogger;
    this.perfMarker = perfMarker;

    const lengthCheckFields = ['TitlePart1', 'TitlePart2', 'Text', 'Path1', 'Path2'];

    this.fields = _.compact(['TitlePart1', 'TitlePart2', 'Text', 'Path1', 'Path2', 'FinalUrls', 'TrackingUrlTemplate', 'UrlCustomParameters', 'MobileFinalUrls', 'FinalUrlSuffix']);

    if (this.IsExpandedTextAdV2Enabled) {
      lengthCheckFields.push('TitlePart3', 'TextPart2');
      this.fields.push('TitlePart3', 'TextPart2');
    }

    if (isNativeAdsEnabled) {
      this.fields.push('NativePreference');
    }

    this.isVanityUrlEnabled = isVanityUrlEnabled;
    if (isVanityUrlEnabled) {
      this.fields.push('DisplayUrl');
    }

    KoSchema.properties(this, ExpandedTextAd, this.fields, _.defaults(model, {
      FinalUrls: [],
    }, isNativeAdsEnabled ? {
      NativePreference: 'NativePreference',
    } : {}));

    dynamicTextExtension.extend(this, lengthCheckFields);

    parsedTextMaxLengthExtension.extend(this, lengthCheckFields);

    this.showNativeAdPreview = ko.computed(() => {
      if (isNativeAdsEnabled) {
        return self.NativePreference.isNativePreference();
      }

      return false;
    }, this);

    if (!isVanityUrlEnabled) {
      this.DisplayUrl = ko.computed(() => {
        return getFinalUrlDomain(_.first(self.FinalUrls()));
      });
    }

    if (IsDisclaimerAdsEnabled) {
      this.disclaimerInfo = ko.observable({});

      this.extensionsSharedRepos = new SharedRepository({
        schema: DisclaimerAdExtension,
        afterChangeComitted: _.noop,
      });

      let disclaimerCampaignId = 0;

      if (Number(this.scope().campaignId) > 0) {
        disclaimerCampaignId = Number(this.scope().campaignId);
      } else if (pageContext.CurrentCampaign && Number(pageContext.CurrentCampaign.Id) > 0) {
        disclaimerCampaignId = Number(pageContext.CurrentCampaign.Id);
      }

      if (disclaimerCampaignId  > 0) {
        this.computeDisclaimerAsync(disclaimerCampaignId);
      }

      this.scope.subscribe((data) => {
        if (data.campaignId) {
          this.computeDisclaimerAsync(data.campaignId);
        }
      });

      this.showDisclaimer =  ko.computed(() => {
        if (this.disclaimerInfo().Id) {
          return true;
        }

        return false;
      });

      this.adDisclaimerHint = ko.computed(() => {
        if (this.disclaimerInfo().Id) {
          if (this.disclaimerInfo().DisclaimerLayout === 'AdditionalLine') {
            return i18n.getString('Disclaimer_Ad_AdditionalLine_Hint');
          } else if (this.disclaimerInfo().DisclaimerLayout === 'Popup') {
            return i18n.getString('Disclaimer_Ad_Popup_Hint');
          }
        }

        return '';
      });
    }

    this.errorMessage = ko.computed(() => {
      if (_.isEmpty(_.result(self.Path1(), 'trim')) && !_.isEmpty(self.Path2())) {
        return i18n.get('ExpandedTextAd_Path1RequiredWhenUsingPath2');
      }

      return '';
    });

    _.each(this.fields, (field) => {
      // log once per field.
      const logChange = _.once(() => {
        if (this.activityLogger) {
          this.activityLogger.signal(`ad field change: ${field}`, 'expanded text ad editor');
        }
      });

      this[field].subscribe(() => {
        logChange();
      }, this);
    });
  }

  computeDisclaimerAsync(disclaimerCampaignId) {
    let params = {
      queryParameters: {
        entitySet: edmCampaign.currentAccount.Campaigns,
      },
      bypassCache: true,
    };

    jsDataCampaign.Campaign.find(disclaimerCampaignId, params)
      .then((data) => {
        const hasDisclaimer =  _.result(_.find(data.CampaignSettings, (setting) => {
          return setting['@odata.type'] === '#Model.DisclaimerSetting';
        }),
        'DisclaimerAdsEnabled');

        if (hasDisclaimer) {
          const scope = Object.create({
            campaignId: disclaimerCampaignId,
          });

          this.extensionsSharedRepos.association.findAll({
            where: scope,
          }).then((disclaimerdata) => {

            if (disclaimerdata && disclaimerdata.length > 0) {
              this.disclaimerInfo(disclaimerdata[0]);
            }
          });
        }
      });
    // TODO : catch and finally
  }

  initPreviewTab() {
    let defaultPreviewType = 'top';

    if (this.showNativeAdPreview()) {
      defaultPreviewType = 'native';
    }

    return defaultPreviewType;
  }
}
